import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { CONTACT, DESTINATIONS } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    travellers: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please provide name & phone", description: "So we can reach out." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({
        title: "Enquiry received!",
        description: `Thanks ${form.name.split(" ")[0]}. Our travel expert will call you within 30 minutes.`,
      });
      setForm({ name: "", email: "", phone: "", destination: "", travellers: "", message: "" });
    }, 900);
  };

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-24 md:pt-48 md:pb-32 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1595815771614-ade9d652a65d?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> Get in Touch
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Let's plan your next journey
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              Tell us where your heart wants to go — we'll build the perfect itinerary in 24 hours. No obligation. No pressure.
            </p>
          </div>
        </section>

        <section className="py-20 md:py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.4fr] gap-10">
            {/* Info Cards */}
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-600 text-white flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Call us</div>
                  <a href={`tel:${CONTACT.phone}`} className="block mt-1 text-lg font-bold text-slate-900 hover:text-teal-700">{CONTACT.phone}</a>
                  <a href={`tel:${CONTACT.altPhone}`} className="block text-sm text-slate-600 hover:text-teal-700">{CONTACT.altPhone}</a>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-600 text-white flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</div>
                  <a href={`mailto:${CONTACT.email}`} className="block mt-1 text-lg font-bold text-slate-900 hover:text-teal-700">{CONTACT.email}</a>
                  <div className="text-sm text-slate-600">Replies within 2 business hours</div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-600 text-white flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Visit us</div>
                  <p className="mt-1 text-slate-800 leading-relaxed">{CONTACT.address}</p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-slate-200 flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</div>
                  <p className="mt-1 text-slate-800 font-semibold">{CONTACT.hours}</p>
                  <p className="text-sm text-slate-500">Emergency helpline always active</p>
                </div>
              </div>
              <a
                href="#"
                className="block bg-gradient-to-r from-teal-700 to-teal-600 text-white p-6 rounded-2xl group hover:from-teal-800 hover:to-teal-700 transition"
              >
                <MessageCircle className="w-6 h-6 mb-3" />
                <div className="font-bold text-lg">Chat on WhatsApp</div>
                <div className="text-sm text-white/85 mt-1">Instant replies from our team · avg 2 min</div>
              </a>
            </div>

            {/* Form */}
            <form onSubmit={submit} className="bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Send us an enquiry
              </h3>
              <p className="mt-2 text-sm text-slate-600">Fill in the details and we'll get back with a custom quote.</p>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                </div>
                <div>
                  <Label htmlFor="travellers">Number of Travellers</Label>
                  <Input id="travellers" value={form.travellers} onChange={(e) => setForm({ ...form, travellers: e.target.value })} placeholder="2 Adults, 1 Child" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Destination Interest</Label>
                  <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a destination" />
                    </SelectTrigger>
                    <SelectContent>
                      {DESTINATIONS.map((d) => (
                        <SelectItem key={d.id} value={d.name}>{d.name}, {d.state}</SelectItem>
                      ))}
                      <SelectItem value="other">Something else / Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message">Your Message</Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Preferred travel dates, budget, special requirements..."
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="mt-6 h-12 px-8 bg-teal-600 hover:bg-teal-700 text-white text-base">
                {loading ? "Sending..." : <>Send Enquiry <Send className="w-4 h-4 ml-2" /></>}
              </Button>
              <p className="mt-3 text-xs text-slate-500">We respect your privacy. No spam — ever.</p>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ContactPage;
